import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { BrowserQRCodeReader } from '@zxing/browser';
import { functions } from '../../firebase-client';
import {
  admissionRosterKey, cacheAdmissionRoster, listQueuedAdmissionActions,
  queueAdmissionAction, readCachedAdmissionRoster, removeQueuedAdmissionAction,
} from '../../utils/admissionOfflineStore';
import {
  admissionActionConfirmation, CHECK_IN_PAGE_SIZES, paginateAdmissionRows,
} from '../../utils/admissionCheckInUi';
import './eventTickets.css';

const loadRosterFn = httpsCallable(functions, 'getEventAdmissionRoster');
const scanFn = httpsCallable(functions, 'scanEventAdmission');
const updateFn = httpsCallable(functions, 'updateEventAdmission');
const loadCourseRosterFn = httpsCallable(functions, 'getCourseAdmissionRoster');
const scanCourseFn = httpsCallable(functions, 'scanCourseAdmission');
const LIVE_ROSTER_REFRESH_MS = 4000;

function expiryForEvent(endsAt) {
  const end = endsAt ? new Date(endsAt).getTime() : NaN;
  return Number.isFinite(end) ? Math.max(Date.now() + 60 * 60 * 1000, end + 36 * 60 * 60 * 1000) : Date.now() + 48 * 60 * 60 * 1000;
}

function createScanSuccessPingUrl() {
  const sampleRate = 16000;
  const duration = 0.24;
  const sampleCount = Math.floor(sampleRate * duration);
  const buffer = new ArrayBuffer(44 + sampleCount * 2);
  const view = new DataView(buffer);
  const write = (offset, value) => {
    for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index));
  };
  write(0, 'RIFF'); view.setUint32(4, 36 + sampleCount * 2, true); write(8, 'WAVE');
  write(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true);
  view.setUint16(22, 1, true); view.setUint32(24, sampleRate, true); view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true); view.setUint16(34, 16, true); write(36, 'data'); view.setUint32(40, sampleCount * 2, true);
  let phase = 0;
  for (let index = 0; index < sampleCount; index += 1) {
    const progress = index / sampleCount;
    const frequency = progress < 0.42 ? 660 : 880;
    phase += (Math.PI * 2 * frequency) / sampleRate;
    const envelope = Math.sin(Math.PI * progress) ** 0.7;
    view.setInt16(44 + index * 2, Math.sin(phase) * envelope * 9000, true);
  }
  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}

function playScanSuccessPing(audio) {
  if (!audio) return;
  try {
    audio.pause();
    audio.currentTime = 0;
    audio.volume = 1;
    audio.play().catch(() => {});
  } catch { /* Sound is optional feedback. */ }
}

export default function EventCheckIn({ sourceType = 'event' }) {
  const { teamId, eventId, courseId } = useParams();
  const [params, setParams] = useSearchParams();
  const sourceId = sourceType === 'course' ? courseId : eventId;
  const occurrenceId = params.get(sourceType === 'course' ? 'session' : 'occurrence') || '';
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanOutcome, setScanOutcome] = useState(null);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [openActionMenu, setOpenActionMenu] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const scanLockRef = useRef(false);
  const processedCredentialsRef = useRef(new Set());
  const scanAudioRef = useRef(null);
  const scanAudioUrlRef = useRef('');
  const rosterRevisionRef = useRef(null);
  const rosterLoadInFlightRef = useRef(false);
  const cacheKey = admissionRosterKey(teamId, `${sourceType}:${sourceId}`, occurrenceId);

  const loadRoster = useCallback(async (options = {}) => {
    const background = options?.background === true;
    if (rosterLoadInFlightRef.current) return;
    rosterLoadInFlightRef.current = true;
    if (!background) setError('');
    if (navigator.onLine) {
      try {
        const payload = sourceType === 'course'
          ? { teamId, courseId: sourceId, sessionId: occurrenceId }
          : { teamId, eventId: sourceId, occurrenceId };
        if (background && rosterRevisionRef.current !== null) payload.knownRevision = rosterRevisionRef.current;
        const loader = sourceType === 'course' ? loadCourseRosterFn : loadRosterFn;
        const result = await loader(payload);
        if (result.data?.notModified) return;
        rosterRevisionRef.current = Number.isFinite(Number(result.data?.revision)) ? Number(result.data.revision) : null;
        setData(result.data); setRows(result.data?.rows || []);
        await cacheAdmissionRoster({ key: cacheKey, rows: result.data?.rows || [], expiresAt: expiryForEvent(result.data?.event?.endsAt) }).catch(() => {});
        return;
      } catch (err) {
        if (!background) setError(err?.message || 'Could not load the check-in list.');
      } finally {
        rosterLoadInFlightRef.current = false;
      }
    }
    rosterLoadInFlightRef.current = false;
    if (background) return;
    const cached = await readCachedAdmissionRoster(cacheKey).catch(() => []);
    if (cached.length) { setRows(cached); setMessage('Using the saved attendee list. Changes will sync when online.'); }
  }, [cacheKey, occurrenceId, sourceId, sourceType, teamId]);

  const syncQueue = useCallback(async () => {
    if (!navigator.onLine) return;
    const pending = await listQueuedAdmissionActions(cacheKey).catch(() => []);
    for (const item of pending) {
      try {
        const updater = sourceType === 'course' ? scanCourseFn : updateFn;
        await updater(item.payload);
        await removeQueuedAdmissionAction(item.id);
      }
      catch (err) { setError(`An offline change needs attention: ${err?.message || 'sync failed'}`); break; }
    }
    if (pending.length) await loadRoster();
  }, [cacheKey, loadRoster, sourceType]);

  useEffect(() => {
    rosterRevisionRef.current = null;
    loadRoster();
  }, [loadRoster]);
  useEffect(() => {
    const refreshLiveRoster = () => {
      if (navigator.onLine && document.visibilityState !== 'hidden') loadRoster({ background: true });
    };
    const intervalId = window.setInterval(refreshLiveRoster, LIVE_ROSTER_REFRESH_MS);
    document.addEventListener('visibilitychange', refreshLiveRoster);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', refreshLiveRoster);
    };
  }, [loadRoster]);
  useEffect(() => {
    const onOnline = () => { setOnline(true); syncQueue(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline); window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, [syncQueue]);
  useEffect(() => () => {
    controlsRef.current?.stop();
    scanAudioRef.current?.pause();
    if (scanAudioUrlRef.current) URL.revokeObjectURL(scanAudioUrlRef.current);
  }, []);
  useEffect(() => {
    if (scanOutcome?.tone !== 'success') return undefined;
    const timer = window.setTimeout(() => {
      setScanOutcome(current => current?.startedAt === scanOutcome.startedAt ? null : current);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [scanOutcome]);
  useEffect(() => {
    const closeMenu = event => {
      if (event.key === 'Escape' || (event.type === 'pointerdown' && !event.target.closest('.checkin-row__menu'))) setOpenActionMenu('');
    };
    document.addEventListener('pointerdown', closeMenu);
    document.addEventListener('keydown', closeMenu);
    return () => {
      document.removeEventListener('pointerdown', closeMenu);
      document.removeEventListener('keydown', closeMenu);
    };
  }, []);

  const applyAction = async (row, action) => {
    setOpenActionMenu('');
    const payload = sourceType === 'course'
      ? { teamId, courseId: sourceId, sessionId: occurrenceId, admissionRef: row.admissionRef, action }
      : { teamId, eventId: sourceId, occurrenceId, admissionRef: row.admissionRef, action };
    setBusy(true); setError(''); setMessage('');
    try {
      if (!navigator.onLine && ['check_in', 'undo'].includes(action)) {
        await queueAdmissionAction({ key: cacheKey, payload });
        setRows(current => current.map(item => item.admissionRef === row.admissionRef ? { ...item, checkedIn: action === 'check_in', offlinePending: true } : item));
        setMessage('Saved offline. This change will sync when the connection returns.');
      } else if (!navigator.onLine) {
        throw new Error('Voiding and reissuing tickets require an internet connection.');
      } else {
        const updater = sourceType === 'course' ? scanCourseFn : updateFn;
        await updater(payload);
        setMessage(action === 'check_in' ? `${row.attendeeLabel} checked in.` : action === 'undo' ? `Check-in undone for ${row.attendeeLabel}.` : action === 'void' ? 'Ticket voided.' : 'Replacement ticket issued and emailed.');
        await loadRoster();
      }
    } catch (err) { setError(err?.message || 'Could not update this ticket.'); }
    finally { setBusy(false); }
  };

  const confirmAndApplyAction = (row, action) => {
    const confirmation = admissionActionConfirmation(action, row.attendeeLabel);
    if (!confirmation || !window.confirm(confirmation)) return;
    applyAction(row, action);
  };

  const handleCredential = useCallback(async credential => {
    if (!credential || scanLockRef.current || processedCredentialsRef.current.has(credential)) return;
    scanLockRef.current = true;
    processedCredentialsRef.current.add(credential);
    setBusy(true); setError(''); setMessage('');
    try {
      const scanner = sourceType === 'course' ? scanCourseFn : scanFn;
      const payload = sourceType === 'course'
        ? { teamId, courseId: sourceId, sessionId: occurrenceId, credential }
        : { teamId, eventId: sourceId, occurrenceId, credential };
      const result = await scanner(payload);
      const admission = result.data?.admission;
      const attendeeLabel = admission?.attendeeLabel || 'Ticket holder';
      const alreadyCheckedIn = result.data?.result === 'already_checked_in';
      const startedAt = Date.now();
      setScanOutcome(alreadyCheckedIn
        ? { tone: 'warning', title: `${attendeeLabel} was already checked in`, detail: 'No additional check-in was recorded.', startedAt }
        : { tone: 'success', title: `${attendeeLabel} checked in!`, detail: 'Ready for the next ticket.', startedAt });
      if (admission?.admissionRef) {
        setRows(current => current.map(row => row.admissionRef === admission.admissionRef
          ? { ...row, ...admission, checkedIn: true }
          : row));
      }
      if (!alreadyCheckedIn && typeof navigator.vibrate === 'function') {
        try { navigator.vibrate(120); } catch { /* Vibration is optional feedback. */ }
      }
      if (!alreadyCheckedIn) playScanSuccessPing(scanAudioRef.current);
    } catch (err) {
      setScanOutcome({ tone: 'error', title: 'Ticket not accepted', detail: err?.message || 'This ticket could not be scanned.', startedAt: Date.now() });
      // Allow a failed credential to be presented again after it has moved away.
      window.setTimeout(() => processedCredentialsRef.current.delete(credential), 2500);
    } finally {
      scanLockRef.current = false;
      setBusy(false);
    }
  }, [occurrenceId, sourceId, sourceType, teamId]);

  const stopScanner = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    scanLockRef.current = false;
    processedCredentialsRef.current.clear();
    setScanning(false);
  };

  const startScanner = async () => {
    if (!online) { setError('QR scanning needs an internet connection. Use manual lookup while offline.'); return; }
    setError(''); setScanOutcome(null); setScanning(true);
    processedCredentialsRef.current.clear();
    try {
      if (!scanAudioRef.current) {
        scanAudioUrlRef.current = createScanSuccessPingUrl();
        scanAudioRef.current = new Audio(scanAudioUrlRef.current);
        scanAudioRef.current.preload = 'auto';
      }
      const audio = scanAudioRef.current;
      audio.volume = 0;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.volume = 1;
    } catch { /* Scanning still works when audio is unavailable. */ }
    try {
      const reader = new BrowserQRCodeReader();
      controlsRef.current = await reader.decodeFromConstraints({
        video: {
          facingMode: { ideal: 'environment' },
          advanced: [{ focusMode: 'continuous' }],
        },
        audio: false,
      }, videoRef.current, result => {
        if (result?.getText()) handleCredential(result.getText());
      });
    } catch (err) { setScanning(false); setError('Camera access is unavailable. Use the attendee search or manual code instead.'); }
  };

  const toggleScanner = () => {
    if (scanning) stopScanner();
    else startScanner();
  };

  const filtered = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return rows;
    return rows.filter(row => `${row.attendeeLabel} ${row.shortCode}`.toLowerCase().includes(value));
  }, [rows, search]);
  const pagination = useMemo(() => paginateAdmissionRows(filtered, page, pageSize), [filtered, page, pageSize]);
  useEffect(() => {
    if (page !== pagination.currentPage) setPage(pagination.currentPage);
  }, [page, pagination.currentPage]);
  const checkedCount = rows.filter(row => row.checkedIn).length;

  return <main className="checkin-page">
    <header className="checkin-page__header"><div><span>{sourceType === 'course' ? 'Course check-in' : 'Event check-in'}</span><h1>{data?.event?.name || 'Admission roster'}</h1><p>{checkedCount} of {rows.length} checked in</p></div><Link className="btn btn--secondary" to={`/teams/${teamId}`}>Back to club</Link></header>
    {!online && <div className="checkin-offline" role="status">Offline: manual changes will be queued. QR scanning{sourceType === 'event' ? ', voiding and reissuing are' : ' is'} unavailable.</div>}
    {sourceType === 'course' && <label className="checkin-occurrence">Course session<select value={occurrenceId} onChange={event => setParams(event.target.value ? { session: event.target.value } : {})}><option value="">Choose a session</option>{(data?.event?.sessions || []).map(session => <option key={session.id} value={session.id}>{session.name}{session.start ? ` · ${new Date(session.start).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}` : ''}</option>)}</select></label>}
    {data?.event?.type === 'recurring' && <label className="checkin-occurrence">Occurrence date<input type="date" value={occurrenceId} onChange={event => setParams(event.target.value ? { occurrence: event.target.value } : {})} /></label>}
    <section className="checkin-scanner">
      <div className={`checkin-scanner__video ${scanning ? 'is-active' : ''}`}><video ref={videoRef} muted playsInline /><span>{scanning ? 'Point the camera at a Member Nest QR ticket' : 'Camera scanner is off'}</span></div>
      <div className="checkin-scanner__controls">
        <button
          type="button"
          className={`checkin-scanner__toggle ${scanning ? 'is-on' : 'is-off'}`}
          disabled={!scanning && !occurrenceId && (data?.event?.type === 'recurring' || sourceType === 'course')}
          aria-pressed={scanning}
          onClick={toggleScanner}
        >
          <span className="checkin-scanner__toggle-icon" aria-hidden="true">{scanning ? '■' : '▶'}</span>
          <span><strong>{scanning ? 'Scanner on' : 'Scanner off'}</strong><small>{scanning ? 'Tap to stop camera' : 'Tap to start scanning'}</small></span>
        </button>
      </div>
    </section>
    {scanOutcome && <section key={scanOutcome.startedAt} className={`checkin-scan-outcome is-${scanOutcome.tone}`} role={scanOutcome.tone === 'error' ? 'alert' : 'status'} aria-live="assertive"><span className="checkin-scan-outcome__icon" aria-hidden="true">{scanOutcome.tone === 'success' ? '✓' : scanOutcome.tone === 'warning' ? '!' : '×'}{scanOutcome.tone === 'success' && <svg className="checkin-scan-outcome__timer" viewBox="0 0 52 52"><circle cx="26" cy="26" r="24" pathLength="100" /></svg>}</span><div><h2>{scanOutcome.title}</h2><p>{scanOutcome.detail}</p></div></section>}
    {message && <p className="checkin-result" role="status">{message}</p>}{error && <p className="error-message" role="alert">{error}</p>}
    <section className="checkin-roster">
      <div className="checkin-roster__tools"><input className="input" type="search" placeholder="Search attendee or ticket code…" value={search} onChange={event => { setSearch(event.target.value); setPage(1); setOpenActionMenu(''); }} /><button type="button" className="btn btn--secondary" onClick={loadRoster} disabled={busy}>Refresh</button></div>
      {filtered.length > 0 && <div className="checkin-pagination checkin-pagination--top"><p>Showing {pagination.startIndex + 1}–{pagination.endIndex} of {filtered.length}</p><label>Per page<select value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); setOpenActionMenu(''); }}>{CHECK_IN_PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}</select></label></div>}
      <ul>{pagination.rows.map(row => <li key={row.admissionRef} className={row.checkedIn ? 'is-checked' : ''}>
        <div className="checkin-row__identity"><strong>{row.attendeeLabel}</strong><span>{row.shortCode} · {row.status === 'active' ? row.checkedIn ? 'Checked in' : 'Not checked in' : 'Void'}{row.offlinePending ? ' · Sync pending' : ''}</span></div>
        <div className="checkin-row__menu">
          <button type="button" className="checkin-row__menu-trigger" aria-label={`Actions for ${row.attendeeLabel}`} aria-haspopup="menu" aria-expanded={openActionMenu === row.admissionRef} disabled={busy} onClick={() => setOpenActionMenu(current => current === row.admissionRef ? '' : row.admissionRef)}>•••</button>
          {openActionMenu === row.admissionRef && <div className="checkin-row__menu-popover" role="menu">
            {row.status === 'active' && <button type="button" role="menuitem" onClick={() => confirmAndApplyAction(row, row.checkedIn ? 'undo' : 'check_in')}>{row.checkedIn ? 'Undo check-in' : 'Check in manually'}</button>}
            {sourceType === 'event' && <button type="button" role="menuitem" className={row.status === 'active' ? 'is-danger' : ''} disabled={!online} onClick={() => confirmAndApplyAction(row, row.status === 'active' ? 'void' : 'reissue')}>{row.status === 'active' ? 'Void admission pass' : 'Reissue admission pass'}</button>}
          </div>}
        </div>
      </li>)}</ul>
      {!filtered.length && <p>No tickets match this search.</p>}
      {filtered.length > 0 && pagination.pageCount > 1 && <nav className="checkin-pagination checkin-pagination--nav" aria-label="Attendee pages"><button type="button" className="btn btn--secondary btn--sm" disabled={pagination.currentPage === 1} onClick={() => { setPage(current => Math.max(1, current - 1)); setOpenActionMenu(''); }}>Previous</button><span>Page {pagination.currentPage} of {pagination.pageCount}</span><button type="button" className="btn btn--secondary btn--sm" disabled={pagination.currentPage === pagination.pageCount} onClick={() => { setPage(current => Math.min(pagination.pageCount, current + 1)); setOpenActionMenu(''); }}>Next</button></nav>}
    </section>
  </main>;
}
